import { StringKey } from '../../../../lang/index';
import { SharePlanType, SharePlanVestingType } from '../../../../types/pool-plans.types';
import { TemplateCategory, TemplateSubCategory } from '../enums/template.const';
import {
  GrantAgreementBaseField,
  grantAgreementBaseFieldLabelKey,
  GrantPlanTypeField,
  grantPlanTypeFieldLabelKey,
  VestingTargetBasedField,
  vestingTargetBasedFieldLabelKey,
  VestingTimeBasedDynamicField,
  vestingTimeBasedDynamicFieldLabelKey,
  VestingTimeBasedStandardField,
  vestingTimeBasedStandardFieldLabelKey,
} from '../enums/template-field.const';

export type FieldOption = {
  labelKey: StringKey;
  labelKeyParams?: Record<string, number>;
  value: string;
};
export type VestingFieldMap = Partial<Record<SharePlanVestingType, FieldOption[]>>;

export type GrantPlanTypeEntry = {
  base: FieldOption[];
  byVestingType?: VestingFieldMap;
};

export type GrantSubCategoryEntry = {
  base: FieldOption[];
  byPlanType?: Partial<Record<SharePlanType, GrantPlanTypeEntry>>;
};

export type GenericSubCategoryEntry = {
  base: FieldOption[];
};

type SubCategoryEntry = GenericSubCategoryEntry | GrantSubCategoryEntry;

type FieldOptionsRegistry = Record<string, Record<string, SubCategoryEntry>>;

const TIME_BASED_STANDARD_FIELDS: FieldOption[] = [
  {
    value: VestingTimeBasedStandardField.VESTING_ON,
    labelKey: vestingTimeBasedStandardFieldLabelKey[VestingTimeBasedStandardField.VESTING_ON],
  },
  {
    value: VestingTimeBasedStandardField.VESTING_DURATION,
    labelKey: vestingTimeBasedStandardFieldLabelKey[VestingTimeBasedStandardField.VESTING_DURATION],
  },
  {
    value: VestingTimeBasedStandardField.VESTING_FREQUENCY,
    labelKey:
      vestingTimeBasedStandardFieldLabelKey[VestingTimeBasedStandardField.VESTING_FREQUENCY],
  },
  {
    value: VestingTimeBasedStandardField.CLIFF,
    labelKey: vestingTimeBasedStandardFieldLabelKey[VestingTimeBasedStandardField.CLIFF],
  },
  {
    value: VestingTimeBasedStandardField.ACCELERATION_DATE,
    labelKey:
      vestingTimeBasedStandardFieldLabelKey[VestingTimeBasedStandardField.ACCELERATION_DATE],
  },
];

const TIME_BASED_DYNAMIC_FIELDS: FieldOption[] = [
  {
    value: VestingTimeBasedDynamicField.VESTING_ON,
    labelKey: vestingTimeBasedDynamicFieldLabelKey[VestingTimeBasedDynamicField.VESTING_ON],
  },
  {
    value: VestingTimeBasedDynamicField.PERIOD_N_DURATION,
    labelKey: vestingTimeBasedDynamicFieldLabelKey[VestingTimeBasedDynamicField.PERIOD_N_DURATION],
  },
  {
    value: VestingTimeBasedDynamicField.PERIOD_N_FREQUENCY,
    labelKey: vestingTimeBasedDynamicFieldLabelKey[VestingTimeBasedDynamicField.PERIOD_N_FREQUENCY],
  },
  {
    value: VestingTimeBasedDynamicField.PERIOD_N_ALLOCATION,
    labelKey:
      vestingTimeBasedDynamicFieldLabelKey[VestingTimeBasedDynamicField.PERIOD_N_ALLOCATION],
  },
  {
    value: VestingTimeBasedDynamicField.ACCELERATION_DATE,
    labelKey: vestingTimeBasedDynamicFieldLabelKey[VestingTimeBasedDynamicField.ACCELERATION_DATE],
  },
];

const TARGET_BASED_FIELDS: FieldOption[] = [
  {
    value: VestingTargetBasedField.MILESTONE_N_NAME,
    labelKey: vestingTargetBasedFieldLabelKey[VestingTargetBasedField.MILESTONE_N_NAME],
  },
  {
    value: VestingTargetBasedField.MILESTONE_N_WEIGHT,
    labelKey: vestingTargetBasedFieldLabelKey[VestingTargetBasedField.MILESTONE_N_WEIGHT],
  },
  {
    value: VestingTargetBasedField.MILESTONE_N_TARGET_DATE,
    labelKey: vestingTargetBasedFieldLabelKey[VestingTargetBasedField.MILESTONE_N_TARGET_DATE],
  },
];

const ALL_VESTING_FIELDS: VestingFieldMap = {
  [SharePlanVestingType.TIME_BASED_STANDARD]: TIME_BASED_STANDARD_FIELDS,
  [SharePlanVestingType.TIME_BASED_DYNAMIC]: TIME_BASED_DYNAMIC_FIELDS,
  [SharePlanVestingType.TARGET_BASED]: TARGET_BASED_FIELDS,
};

const vestingOnlyEntry: GrantPlanTypeEntry = { base: [], byVestingType: ALL_VESTING_FIELDS };

export const FIELD_OPTIONS_REGISTRY: FieldOptionsRegistry = {
  [TemplateCategory.GRANT]: {
    [TemplateSubCategory.GRANT_AGREEMENT]: {
      base: [
        {
          value: GrantAgreementBaseField.STAKEHOLDER_NAME,
          labelKey: grantAgreementBaseFieldLabelKey[GrantAgreementBaseField.STAKEHOLDER_NAME],
        },
        {
          value: GrantAgreementBaseField.STAKEHOLDER_EMAIL,
          labelKey: grantAgreementBaseFieldLabelKey[GrantAgreementBaseField.STAKEHOLDER_EMAIL],
        },
        {
          value: GrantAgreementBaseField.PASSPORT_NUMBER,
          labelKey: grantAgreementBaseFieldLabelKey[GrantAgreementBaseField.PASSPORT_NUMBER],
        },
        {
          value: GrantAgreementBaseField.NATIONALITY,
          labelKey: grantAgreementBaseFieldLabelKey[GrantAgreementBaseField.NATIONALITY],
        },
        {
          value: GrantAgreementBaseField.ADDRESS,
          labelKey: grantAgreementBaseFieldLabelKey[GrantAgreementBaseField.ADDRESS],
        },
        {
          value: GrantAgreementBaseField.COMPANY_IDENTIFIER,
          labelKey: grantAgreementBaseFieldLabelKey[GrantAgreementBaseField.COMPANY_IDENTIFIER],
        },
        {
          value: GrantAgreementBaseField.INCORPORATION_COUNTRY,
          labelKey: grantAgreementBaseFieldLabelKey[GrantAgreementBaseField.INCORPORATION_COUNTRY],
        },
        {
          value: GrantAgreementBaseField.COMPANY_NAME,
          labelKey: grantAgreementBaseFieldLabelKey[GrantAgreementBaseField.COMPANY_NAME],
        },
        {
          value: GrantAgreementBaseField.GRANT_DATE,
          labelKey: grantAgreementBaseFieldLabelKey[GrantAgreementBaseField.GRANT_DATE],
        },
        {
          value: GrantAgreementBaseField.GRANT_AMOUNT,
          labelKey: grantAgreementBaseFieldLabelKey[GrantAgreementBaseField.GRANT_AMOUNT],
        },
        {
          value: GrantAgreementBaseField.PLAN_NAME,
          labelKey: grantAgreementBaseFieldLabelKey[GrantAgreementBaseField.PLAN_NAME],
        },
        {
          value: GrantAgreementBaseField.PLAN_TYPE,
          labelKey: grantAgreementBaseFieldLabelKey[GrantAgreementBaseField.PLAN_TYPE],
        },
        {
          value: GrantAgreementBaseField.VESTING_TYPE,
          labelKey: grantAgreementBaseFieldLabelKey[GrantAgreementBaseField.VESTING_TYPE],
        },
      ],
      byPlanType: {
        [SharePlanType.WARRANTS]: {
          base: [
            {
              value: GrantPlanTypeField.WARRANT_PRICE,
              labelKey: grantPlanTypeFieldLabelKey[GrantPlanTypeField.WARRANT_PRICE],
            },
          ],
          byVestingType: ALL_VESTING_FIELDS,
        },

        [SharePlanType.GROWTH_HURDLE]: {
          base: [
            {
              value: GrantPlanTypeField.HURDLE_VALUE,
              labelKey: grantPlanTypeFieldLabelKey[GrantPlanTypeField.HURDLE_VALUE],
            },
          ],
          byVestingType: ALL_VESTING_FIELDS,
        },

        [SharePlanType.STOCK_OPTIONS]: {
          base: [
            {
              value: GrantPlanTypeField.STRIKE_PRICE,
              labelKey: grantPlanTypeFieldLabelKey[GrantPlanTypeField.STRIKE_PRICE],
            },
          ],
          byVestingType: ALL_VESTING_FIELDS,
        },

        [SharePlanType.RSA]: {
          base: [
            {
              value: GrantPlanTypeField.RSA_PURCHASE_PRICE,
              labelKey: grantPlanTypeFieldLabelKey[GrantPlanTypeField.RSA_PURCHASE_PRICE],
            },
          ],
          byVestingType: ALL_VESTING_FIELDS,
        },

        [SharePlanType.BSPCE]: {
          base: [
            {
              value: GrantPlanTypeField.STRIKE_PRICE,
              labelKey: grantPlanTypeFieldLabelKey[GrantPlanTypeField.STRIKE_PRICE],
            },
          ],
          byVestingType: ALL_VESTING_FIELDS,
        },

        [SharePlanType.PHANTOM]: vestingOnlyEntry,
        [SharePlanType.COMMON_STOCK]: vestingOnlyEntry,
        [SharePlanType.RSU]: vestingOnlyEntry,
        [SharePlanType.SAR]: vestingOnlyEntry,
      },
    },
  },
};
